import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { UserProfile } from '@vsp/angular-core-libraries/authentication/user-profile';
import { UserGroups } from '@vsp/angular-core-libraries/authentication/user-groups';
import { RouterEventsService } from '@vsp/angular-core-libraries/authentication/router-events/router-events.service';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationServiceImpl {
  private static readonly LOGIN_COMMAND = {
    type: 'login',
  };
  private static readonly LOGOUT_COMMAND = {
    type: 'logout',
  };
  authenticationHeader?: HttpHeaders;
  public userToken!: string;
  public userProfile?: UserProfile;
  public loggedIn = false;
  public userGroups?: UserGroups = undefined;
  private userInfoApiObservable!: Subscription;
  private jwtHelperService: JwtHelperService;
  private tokenSubject = new BehaviorSubject<string>('');
  private userSubject = new BehaviorSubject<UserProfile>({} as UserProfile);
  private userGroupsSubject = new BehaviorSubject<UserGroups>({} as UserGroups);
  private acsLoginUrl: string | null;
  private acsSubscription?: Subscription;
  private routerSubscription?: Subscription;
  public _isKaleyedoscopePractice = false;
  getPrincipal: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _principal = '';

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    @Inject('openIdUrl') private openIdUrl: string,
    @Inject('oauthApiUrl') private oauthApiUrl: string,
    @Inject('oauthClientId') private oauthClientId: string,
    @Inject('oauthScopes') private oauthScopes: string,
    @Inject('oauthRedirectUrl') private oauthRedirectUrl: string,
    private cookieService: CookieService
  ) {
    this.jwtHelperService = new JwtHelperService();
    this.acsLoginUrl = this.sanitizer.sanitize(
      SecurityContext.URL,
      [
        this.oauthApiUrl,
        '/acs2/login?client_id=',
        this.oauthClientId,
        '&scope=',
        this.oauthScopes,
        '&redirect_uri=',
        this.oauthRedirectUrl,
        '&target=',
        environment.cloudTarget,
      ].join('')
    );
    this.tokenSubject.subscribe(userToken => {
      this.userToken = userToken;
      if (this.userToken === '') {
        this.userSubject.next({} as UserProfile);
      }
    });
  }

  private static executeAction(commandMessage: { type: string }): void {
    const acsApiElement: HTMLIFrameElement = document.getElementById('acs-api') as HTMLIFrameElement;
    const acsApiIframe = acsApiElement.contentWindow;
    acsApiIframe?.postMessage(JSON.stringify(commandMessage), '*');
  }

  public onTokenRefresh(): BehaviorSubject<string> {
    return this.tokenSubject;
  }

  public getToken(): string {
    return this.userToken ? this.userToken : '';
  }

  public isLoggedIn(): boolean {
    return this.loggedIn;
  }

  getUser(): BehaviorSubject<UserProfile> {
    return this.userSubject;
  }

  setUser(user: UserProfile | null): void {
    this.userProfile = user === null ? ({} as UserProfile) : user;
    if (user) {
      this.userSubject.next(user);
    }
  }

  getUserGroups(): BehaviorSubject<UserGroups> {
    return this.userGroupsSubject;
  }

  setUserGroups(userGroups: UserGroups): void {
    if (this.userGroups === undefined && userGroups) {
      this.userGroups = userGroups;
      this.userGroupsSubject.next(userGroups);
    }
  }

  setACSUrl(path: string): void {
    this.acsLoginUrl = this.sanitizer.sanitize(
      SecurityContext.URL,
      [this.oauthApiUrl, '/acs2/login?client_id=', this.oauthClientId, '&scope=', this.oauthScopes, '&target=', environment.cloudTarget, '&redirect_uri=', path].join('')
    );
  }

  public login(path?: string): void {
    if (path) {
      this.setACSUrl(path);
    }
    // If the VSPIDC_ACCESS cookie is null or missing in the log in route the user to the welcome page.
    // Else navigate them to the PingFed log in page.
    if (!this.vspAcessCookieExists()) {
      // redirect to the logon page
      window.location.href = environment.welcomeUrl;
    } else {
      if (typeof this.acsLoginUrl === 'string') {
        // redirect to the PingFed logon page
        document.location.href = encodeURI(this.acsLoginUrl);
      }
    }
  }

  /**
   * Checks if the VSPIDC_ACCESS cookie exists or if the application is running in a local environment.
   *
   * @returns {boolean} - Returns `true` if the application is running in a local environment or
   *                      if the `VSPIDC_ACCESS` cookie exists and is not empty. Otherwise, returns `false`.
   */
  public vspAcessCookieExists(): boolean {
    // Determine if the application is running in a local environment by checking if the
    // `oauthRedirectUrl` in the environment configuration contains 'localhost'.
    const isLocalEnvironment = environment.oauthRedirectUrl.includes('localhost');

    // If running in a local environment, return true.
    if (isLocalEnvironment) {
      return true;
    } else {
      // Otherwise, check if the `VSPIDC_ACCESS` cookie exists and is not empty.
      return this.cookieService.get('VSPIDC_ACCESS') !== null && this.cookieService.get('VSPIDC_ACCESS').length !== 0;
    }
  }

  public logout(): void {
    if (this.acsSubscription) {
      this.acsSubscription.unsubscribe();
    }
    this.userToken = '';
    this.tokenSubject.next('');
    this.userSubject.next({} as UserProfile);
    this.loggedIn = false;
    this.userGroups = undefined;
    this.userGroupsSubject.next({} as UserGroups);
    AuthenticationServiceImpl.executeAction(AuthenticationServiceImpl.LOGOUT_COMMAND);
  }

  public isAuthenticated(): boolean {
    // Check whether the token is expired and return result
    return !this.jwtHelperService.isTokenExpired(this.userToken);
  }

  public receiveToken(token: string): void {
    this.userToken = token;
    this.tokenSubject.next(token);
    this.loggedIn = true;
    const decodedAccessToken = this.jwtHelperService.decodeToken(this.userToken);
    this.setUser(decodedAccessToken);
    const inventoryProgram = decodedAccessToken.inventory_program;
    if (inventoryProgram === 'kaleyedoscope') {
      this.isKaleyedoscopePractice = true;
    }
    this.principal = decodedAccessToken?.principal || '';
    this.getPrincipal.next(true);

    // TODO: add ability to restrict user groups
    if (this.userGroups === undefined) {
      this.retrieveUserGroups();
    }
    this.authenticationHeader = new HttpHeaders({
      Authorization: this.userToken,
    });
  }

  retrieveUserGroups(): void {
    if (this.userInfoApiObservable === null) {
      // @ts-ignore
      this.userInfoApiObservable = this.http
        .get(this.openIdUrl, {
          headers: new HttpHeaders().set('Authorization', this.userToken),
        })
        // @ts-ignore
        .subscribe((userGroups: UserGroups) => {
          this.setUserGroups(userGroups);
        });
    }
  }

  /*
   * This method checks if the user landed on this page via a page load or a router link.
   * If coming from a router link would already have the ACS iframe initiated so we shouldn't wait around for a
   * message. Instead since we already checked that the user is not logged in we know they need to be authenticated still.
   * If coming from a page load we need to wait for the ACS iframe to be initialized and check if it comes back with
   * a token or a blank, which will indicate if we are authenticated or not.
   */
  public checkAuthentication(url?: string, routerEventsService?: RouterEventsService): Observable<boolean> {
    //this.spinnerService.updateShowLoadingIndicator(true);
    return new Observable(observer => {
      if (routerEventsService) {
        this.routerSubscription = routerEventsService.routeChanged.subscribe(updatedRoutes => {
          // check if navigating via routerlink, if so then login since an acs iFrame message won't happen
          if (updatedRoutes.prevRoute !== '/oauth_callback') {
            this.login(url);
            observer.next(false);
            observer.complete();
          }
        });
      }

      // this is a navigation via page load, check if iFrame message has been recieved and has a token
      const msgEvent = fromEvent(window, 'message').pipe(
        filter(({ origin }: Partial<MessageEvent> = { origin: '' }) =>
          // @ts-ignore
          origin.includes(this.oauthApiUrl)
        ), // @ts-ignore
        map(event => event.data)
      );

      // we need to save the subscription so we can unsubscribe on logout
      this.acsSubscription = msgEvent.subscribe((token: string) => {
        if (token) {
          if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
          }
          this.receiveToken(token);
          //this.spinnerService.updateShowLoadingIndicator(false);
          observer.next(true);
          observer.complete();
        } else {
          this.login(url);
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Extracts the `efan` from the provided token.
   * @param token The JWT token.
   * @returns The `efan` value or `null` if not found.
   */
  public getEfanFromToken(token: string): string | null {
    if (!token) {
      console.error('Token is required');
      return null;
    }

    try {
      const decodedToken = this.jwtHelperService.decodeToken(token);
      return decodedToken?.efan || null;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  get isKaleyedoscopePractice(): boolean {
    return this._isKaleyedoscopePractice;
  }

  set isKaleyedoscopePractice(value: boolean) {
    this._isKaleyedoscopePractice = value;
  }

  public get principal(): string {
    return this._principal;
  }

  public set principal(value: string) {
    this._principal = value;
  }
}
